// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { Anchor, Box, Button, Group, Image, Loader, Paper, Stack, Table, Text, TextInput, Title } from '@mantine/core';
import { getReferenceString } from '@medplum/core';
import type { Communication, DiagnosticReport, ServiceRequest } from '@medplum/fhirtypes';
import { useMedplum, useMedplumProfile } from '@medplum/react';
import { HSAT_ORDERED_DATE_EXTENSION_URL, HSAT_STATUS_LABELS, HSAT_STATUS_TAG_SYSTEM } from '@polarissleep/shared';
import { IconSearch } from '@tabler/icons-react';
import dayjs from 'dayjs';
import { type JSX, useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import watchpatDevice from '../../assets/watchpat-device.png';

function formatDate(value: string | undefined): string {
  if (!value) {
    return '—';
  }
  return dayjs(value).format('MM/DD/YYYY');
}

function getTestName(_sr: ServiceRequest): string {
  return 'HSAT';
}

function getHsatStatus(sr: ServiceRequest): string {
  const tag = sr.meta?.tag?.find((t) => t.system === HSAT_STATUS_TAG_SYSTEM);
  if (tag?.code) {
    return HSAT_STATUS_LABELS[tag.code] ?? tag.display ?? tag.code;
  }
  return sr.status ?? '—';
}

function getDiagnosisCode(sr: ServiceRequest): string {
  return sr.reasonCode?.[0]?.coding?.[0]?.code ?? '—';
}

function getOrderedDate(sr: ServiceRequest): string {
  return formatDate(sr.extension?.find((e) => e.url === HSAT_ORDERED_DATE_EXTENSION_URL)?.valueDateTime);
}

function hasResults(sr: ServiceRequest, reports: DiagnosticReport[]): boolean {
  const ref = `ServiceRequest/${sr.id}`;
  return reports.some((r) => r.status === 'final' && r.basedOn?.some((b) => b.reference === ref));
}

function getReportForSr(sr: ServiceRequest, reports: DiagnosticReport[]): DiagnosticReport | undefined {
  const ref = `ServiceRequest/${sr.id}`;
  return reports.find((r) => r.basedOn?.some((b) => b.reference === ref));
}

export interface TestsAndLabsSectionProps {
  hsatOrders: ServiceRequest[];
  loading: boolean;
  orderedServiceRequest?: ServiceRequest;
  diagnosticReports: DiagnosticReport[];
}

export function TestsAndLabsSection({
  hsatOrders,
  loading,
  orderedServiceRequest,
  diagnosticReports,
}: TestsAndLabsSectionProps): JSX.Element {
  const medplum = useMedplum();
  const navigate = useNavigate();
  const profile = useMedplumProfile();
  const [query, setQuery] = useState('');
  const [supportLoading, setSupportLoading] = useState(false);

  const goToSupport = useCallback(async () => {
    setSupportLoading(true);
    try {
      const recipientRef = profile ? getReferenceString(profile) : undefined;
      const params = new URLSearchParams();
      params.set('topic:missing', 'false');
      params.set('part-of:missing', 'true');
      params.set('status', 'in-progress');
      params.set('_count', '20');
      params.set('_sort', 'sent');
      if (recipientRef) {
        params.set('recipient', recipientRef);
      }
      const bundle = await medplum.search('Communication', params.toString());
      const communications = bundle.entry?.map((e) => e.resource as Communication).filter(Boolean) ?? [];
      const first = communications.find((c) => !c.payload?.length);
      const qs = '?_count=20&_sort=-_lastUpdated&_total=accurate&status=in-progress&bucket=all';
      if (first?.id) {
        navigate(`/messages/${first.id}${qs}`);
      } else {
        navigate(`/messages${qs}`);
      }
    } finally {
      setSupportLoading(false);
    }
  }, [medplum, navigate, profile]);

  const openReportPdf = useCallback(
    async (sr: ServiceRequest) => {
      const rawUrl = getReportForSr(sr, diagnosticReports)?.presentedForm?.[0]?.url;
      if (!rawUrl) {
        return;
      }
      const href = rawUrl.startsWith('Binary/') ? URL.createObjectURL(await medplum.download(rawUrl)) : rawUrl;
      window.open(href, '_blank', 'noopener,noreferrer');
    },
    [medplum, diagnosticReports]
  );

  const orderedReport = orderedServiceRequest ? getReportForSr(orderedServiceRequest, diagnosticReports) : undefined;

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) {
      return hsatOrders;
    }
    return hsatOrders.filter((sr) =>
      `${getTestName(sr)} ${getHsatStatus(sr)} ${sr.requester?.display ?? ''}`.toLowerCase().includes(q)
    );
  }, [hsatOrders, query]);

  return (
    <Paper withBorder radius="md" shadow="sm" p="xl">
      <Group justify="space-between" mb="md" wrap="wrap">
        <Title order={2}>Tests &amp; Labs</Title>
        <TextInput
          placeholder="Search…"
          value={query}
          onChange={(e) => setQuery(e.currentTarget.value)}
          leftSection={<IconSearch size={14} />}
          size="sm"
          w={240}
          aria-label="Search tests and labs"
        />
      </Group>

      {loading ? (
        <Stack align="center" py="md">
          <Loader size="sm" />
        </Stack>
      ) : (
        <>
          <Box style={{ overflowX: 'auto' }} mb={orderedServiceRequest ? 'md' : 0}>
            <Table highlightOnHover withTableBorder>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Test</Table.Th>
                  <Table.Th>Status</Table.Th>
                  <Table.Th>Ordered by</Table.Th>
                  <Table.Th>Order Date</Table.Th>
                  <Table.Th>Suspected Diagnoses</Table.Th>
                  <Table.Th>Actions</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {filtered.length === 0 ? (
                  <Table.Tr>
                    <Table.Td colSpan={6}>
                      <Text ta="center" c="dimmed" py="md">
                        No tests or labs on file.
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                ) : (
                  filtered.map((sr) => (
                    <Table.Tr key={sr.id}>
                      <Table.Td>{getTestName(sr)}</Table.Td>
                      <Table.Td>{getHsatStatus(sr)}</Table.Td>
                      <Table.Td>
                        <Anchor size="xs" c="polaris" fw={600}>
                          {sr.requester?.display ?? '—'}
                        </Anchor>
                      </Table.Td>
                      <Table.Td>{getOrderedDate(sr)}</Table.Td>
                      <Table.Td>{getDiagnosisCode(sr)}</Table.Td>
                      <Table.Td>
                        <Group gap={8} wrap="nowrap">
                          {hasResults(sr, diagnosticReports) && (
                            <Anchor
                              size="xs"
                              c="polaris"
                              fw={600}
                              component="button"
                              type="button"
                              onClick={() => {
                                openReportPdf(sr).catch(console.error);
                              }}
                            >
                              View Results
                            </Anchor>
                          )}
                          <Anchor
                            size="xs"
                            c="polaris"
                            fw={600}
                            component="button"
                            type="button"
                            disabled={supportLoading}
                            onClick={() => {
                              goToSupport().catch(console.error);
                            }}
                          >
                            Get Support
                          </Anchor>
                        </Group>
                      </Table.Td>
                    </Table.Tr>
                  ))
                )}
              </Table.Tbody>
            </Table>
          </Box>

          {orderedServiceRequest && !orderedReport && (
            <Paper withBorder radius="md" p="md">
              <Group gap="md" align="stretch" wrap="nowrap">
                <Image src={watchpatDevice} alt="WatchPAT One device" radius="md" h={97} w={97} fit="cover" />
                <Stack gap={4} style={{ flex: 1, minWidth: 0 }}>
                  <Title order={3}>Test Ordered</Title>
                </Stack>
                <Paper withBorder radius="md" px="md" py="xs" style={{ minWidth: 120 }}>
                  <Stack gap={4}>
                    <Text fw={700} size="sm">
                      Order Status
                    </Text>
                    <Text size="sm">{getHsatStatus(orderedServiceRequest)}</Text>
                  </Stack>
                </Paper>
              </Group>
            </Paper>
          )}

          {orderedServiceRequest && orderedReport && (
            <Paper
              withBorder
              radius="md"
              p="md"
              style={{
                backgroundColor:
                  orderedReport.status === 'final' ? 'var(--polaris-light-purple)' : 'var(--mantine-color-gray-0)',
                borderColor: orderedReport.status === 'final' ? 'var(--mantine-color-polaris-5)' : undefined,
              }}
            >
              <Group gap="md" align="stretch" wrap="nowrap">
                <Image src={watchpatDevice} alt="WatchPAT One device" radius="md" h={97} w={97} fit="cover" />
                <Stack gap={4} style={{ flex: 1, minWidth: 0 }}>
                  {orderedReport.status === 'final' && <Title order={3}>Results Available!</Title>}
                  {orderedReport.status === 'partial' && <Title order={3}>Test Complete</Title>}
                  {orderedReport.status === 'partial' && (
                    <Text size="sm">
                      WatchPAT has received your test and is assessing the results. We will notify you when they are
                      available.
                    </Text>
                  )}
                  {orderedReport.status === 'final' && <Text size="sm">Your test results are available!</Text>}
                </Stack>
                {orderedReport.status === 'final' && (
                  <Button
                    style={{
                      backgroundColor: 'var(--mantine-color-polaris-5)',
                      alignSelf: 'center',
                      fontSize: 18,
                      paddingLeft: 24,
                      paddingRight: 24,
                      paddingTop: 12,
                      paddingBottom: 12,
                      height: 50,
                    }}
                    onClick={() => navigate('/appointments/schedule')}
                  >
                    Schedule Diagnostic Appointment
                  </Button>
                )}
                {orderedReport.status === 'partial' && (
                  <Paper withBorder radius="md" px="md" py="xs" style={{ minWidth: 120, backgroundColor: 'white' }}>
                    <Stack gap={4}>
                      <Text fw={700} size="sm" style={{ color: '#868E96' }}>
                        Order Status
                      </Text>
                      <Text size="sm" style={{ color: '#868E96' }}>
                        Complete
                      </Text>
                    </Stack>
                  </Paper>
                )}
                {orderedReport.status === 'partial' && (
                  <Paper withBorder radius="md" px="md" py="xs" style={{ minWidth: 120, backgroundColor: 'white' }}>
                    <Stack gap={4}>
                      <Text fw={700} size="sm" style={{ color: '#868E96' }}>
                        Test Status
                      </Text>
                      <Text size="sm" style={{ color: '#868E96' }}>
                        Complete
                      </Text>
                    </Stack>
                  </Paper>
                )}
              </Group>
            </Paper>
          )}
        </>
      )}
    </Paper>
  );
}
